import React, { useState, useEffect, useCallback } from 'react';

const slides = [
  // Slide 1: Title
  {
    id: 1,
    content: (
      <div className="slide">
        <div className="slide-number">1/20</div>
        <h1>GusHome</h1>
        <p className="tagline">The First Stock Market for Real Estate</p>
        <div className="highlight">
          <h3>Pre-Seed Investment Opportunity</h3>
          <p style={{ textAlign: 'center', fontSize: '1.4rem' }}>$4.8M to validate the NASDAQ for apartments</p>
        </div>
        <div className="cta">
          <p style={{ fontSize: '1.1rem', marginBottom: '20px' }}>June 2025 • 12-Month NYC Pilot Validation Strategy</p>
          <p style={{ fontSize: '1rem', opacity: '0.9' }}>Confidential & Proprietary</p>
        </div>
      </div>
    )
  },

  // Slide 2: The Problem
  {
    id: 2,
    content: (
      <div className="slide">
        <div className="slide-number">2/20</div>
        <h2>The Problem</h2>
        <div className="metric-grid">
          <div className="metric-card">
            <div className="metric-number">$650B</div>
            <div className="metric-label">Annual rent payments generating ZERO equity</div>
          </div>
          <div className="metric-card">
            <div className="metric-number">46M</div>
            <div className="metric-label">Rental households building no wealth</div>
          </div>
          <div className="metric-card">
            <div className="metric-number">90 days</div>
            <div className="metric-label">Real estate sales vs. instant stock trades</div>
          </div>
        </div>
        <div className="value-prop">
          <h4>Real estate is the last asset class without stock market liquidity</h4>
          <p>Americans understand buying Tesla stock, but can't easily invest in the apartment building next door.</p>
        </div>
      </div>
    )
  },

  // Slide 3: Our Solution
  {
    id: 3,
    content: (
      <div className="slide">
        <div className="slide-number">3/20</div>
        <h2>Our Solution: Real Estate Stock Exchange</h2>
        <div className="three-column-layout">
          <div className="value-prop">
            <h4>🏠 For Renters</h4>
            <p>"Turn your rent into ownership"</p>
            <p>Build equity in your home like buying stock</p>
          </div>
          <div className="value-prop">
            <h4>📈 For Investors</h4>
            <p>"Own properties like Tesla stock"</p>
            <p>Buy/sell real estate shares with market pricing</p>
          </div>
          <div className="value-prop">
            <h4>🏢 For Landlords</h4>
            <p>"Your property becomes a stock"</p>
            <p>Access equity liquidity when market demand exists</p>
          </div>
        </div>
        <div className="highlight">
          <h4>Core Innovation: Stock Market Mechanics for Real Estate</h4>
          <p>Properties trade like stocks with market-driven pricing, buyer-seller matching, and organic liquidity development.</p>
        </div>
      </div>
    )
  },

  // Slide 4: Market Opportunity
  {
    id: 4,
    content: (
      <div className="slide">
        <div className="slide-number">4/20</div>
        <h2>Massive Market Opportunity</h2>
        <div className="metric-grid">
          <div className="metric-card">
            <div className="metric-number">$2.1T</div>
            <div className="metric-label">Total addressable market for fractional real estate</div>
          </div>
          <div className="metric-card">
            <div className="metric-number">55M</div>
            <div className="metric-label">Active stock traders familiar with marketplace mechanics</div>
          </div>
          <div className="metric-card">
            <div className="metric-number">40M+</div>
            <div className="metric-label">Americans already understand stock trading apps</div>
          </div>
        </div>
        <div className="highlight">
          <h4>NYC Pilot Market Focus</h4>
          <p>2.1M rental households • $3,200 average rent • Tech-savvy population with mobile-first investment experience</p>
        </div>
      </div>
    )
  },

  // Slide 5: How It Works - Three-Sided Marketplace
  {
    id: 5,
    content: (
      <div className="slide">
        <div className="slide-number">5/20</div>
        <h2>How It Works: Three-Sided Marketplace</h2>
        
        <div className="three-column-layout">
          <div className="value-prop">
            <h4>1. Landlords List Shares</h4>
            <ul>
              <li>Access equity liquidity</li>
              <li>Market-based pricing</li>
              <li>Retain rental income</li>
            </ul>
          </div>
          
          <div className="value-prop">
            <h4>2. Renters Build Equity</h4>
            <ul>
              <li>5% of rent auto-invested</li>
              <li>Purchase at market value</li>
              <li>Become property co-owners</li>
            </ul>
          </div>
          
          <div className="value-prop">
            <h4>3. Investors Trade</h4>
            <ul>
              <li>Browse with financial metrics</li>
              <li>Buy/sell at market prices</li>
              <li>Portfolio across properties</li>
            </ul>
          </div>
        </div>

        <div className="highlight">
          <h4>Network Effects: More participants = better liquidity for everyone</h4>
        </div>
      </div>
    )
  },

  // Slide 6: Stock Market Model Applied to Real Estate
  {
    id: 6,
    content: (
      <div className="slide">
        <div className="slide-number">6/20</div>
        <h2>Stock Market Model Applied to Real Estate</h2>
        
        <div className="two-column-layout">
          <div className="competitive-advantage">
            <h4>Price Discovery Mechanism</h4>
            <ul>
              <li>Zillow API Integration: Real-time property valuations</li>
              <li>Market-driven pricing through buyer/seller activity</li>
              <li>Transparent pricing with transaction history</li>
            </ul>
          </div>
          
          <div className="competitive-advantage">
            <h4>Liquidity Development Strategy</h4>
            <ul>
              <li>No Guaranteed Buyers: Like stocks, sales depend on market demand</li>
              <li>Network Effects: More participants = better liquidity</li>
              <li>Organic marketplace growth creates trading opportunities</li>
            </ul>
          </div>
        </div>
        
        <div className="highlight">
          <h4>Example: Property Share Pricing</h4>
          <p>$500K Property → 1,000 shares at $500 each<br/>
          Property appreciates to $550K → Shares now $550 each (+10%)</p>
        </div>
      </div>
    )
  },

  // Slide 7: NYC Pilot Strategy
  {
    id: 7,
    content: (
      <div className="slide">
        <div className="slide-number">7/20</div>
        <h2>NYC Pilot Strategy: Partnership-Driven Validation</h2>
        
        <div className="metric-grid">
          <div className="metric-card">
            <div className="metric-number">25</div>
            <div className="metric-label">Landlord partners with 10+ properties each</div>
          </div>
          <div className="metric-card">
            <div className="metric-number">375</div>
            <div className="metric-label">Total properties in pilot</div>
          </div>
          <div className="metric-card">
            <div className="metric-number">40%+</div>
            <div className="metric-label">Target tenant opt-in rate</div>
          </div>
        </div>
        
        <div className="value-prop">
          <h4>Critical Validation Questions We'll Answer:</h4>
          <ul>
            <li>Do tenants choose equity building when offered choice?</li>
            <li>Can real estate achieve stock-like liquidity?</li>
            <li>Do landlords participate at scale?</li>
            <li>Are unit economics viable?</li>
          </ul>
        </div>
      </div>
    )
  },

  // Slide 8: Competitive Advantages
  {
    id: 8,
    content: (
      <div className="slide">
        <div className="slide-number">8/20</div>
        <h2>Unique Competitive Advantages</h2>
        
        <div className="three-column-layout">
          <div className="competitive-advantage">
            <h4>1. Stock Market Mental Model</h4>
            <p>40+ million Americans already comfortable with stock trading apps. Reduced education costs and faster adoption.</p>
          </div>
          
          <div className="competitive-advantage">
            <h4>2. Three-Sided Network Effects</h4>
            <p>More landlords = more choices. More renters = more demand. More investors = better liquidity.</p>
          </div>

          <div className="competitive-advantage">
            <h4>3. Market-Based Price Discovery</h4>
            <p>Real-time market valuations vs. opaque real estate pricing. Transparent, efficient pricing reduces information asymmetry.</p>
          </div>
        </div>
        
        <div className="highlight">
          <h4>Patent Protection: 24+ patents covering marketplace mechanics and three-sided model</h4>
        </div>
      </div>
    )
  },

  // Slide 9: Competition Analysis
  {
    id: 9,
    content: (
      <div className="slide">
        <div className="slide-number">9/20</div>
        <h2>Competitive Landscape</h2>
        
        <div className="financial-table">
          <table>
            <thead>
              <tr>
                <th>Platform</th>
                <th>Model</th>
                <th>Min Investment</th>
                <th>Liquidity</th>
                <th>User Control</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>GusHome</strong></td>
                <td>Stock market marketplace</td>
                <td>$75/month</td>
                <td>Market-based trading</td>
                <td>Advisory voting</td>
              </tr>
              <tr>
                <td>Arrived Homes</td>
                <td>Pre-purchased securities</td>
                <td>$100</td>
                <td>Company buyback</td>
                <td>None</td>
              </tr>
              <tr>
                <td>Fundrise</td>
                <td>eREIT diversification</td>
                <td>$500</td>
                <td>Quarterly redemption</td>
                <td>None</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div className="highlight">
          <h4>Our Advantage: "The Robinhood for Real Estate"</h4>
          <p>Familiar stock market mechanics, user choice in property selection, and transparent market-driven pricing.</p>
        </div>
      </div>
    )
  },

  // Slide 10: Technology Infrastructure
  {
    id: 10,
    content: (
      <div className="slide">
        <div className="slide-number">10/20</div>
        <h2>Technology Infrastructure</h2>
        
        <div className="two-column-layout">
          <div className="value-prop">
            <h4>Core Platform ($750K Development)</h4>
            <ul>
              <li>Native iOS/Android apps</li>
              <li>Backend with SOC 2 compliance</li>
              <li>Mobile-first design</li>
            </ul>
          </div>
          
          <div className="value-prop">
            <h4>Third-Party Integrations ($312K Annual)</h4>
            <ul>
              <li>Zillow Premium API</li>
              <li>Stripe Payment Processing</li>
              <li>AWS Cloud Infrastructure</li>
            </ul>
          </div>
        </div>
        
        <div className="highlight">
          <h4>Risk Mitigation: Multiple Backup Systems</h4>
          <p>CoreLogic, Realtor.com backup APIs • Square payment backup • Proprietary valuation algorithm development</p>
        </div>
      </div>
    )
  },

  // Slide 11: Business Model & Revenue
  {
    id: 11,
    content: (
      <div className="slide">
        <div className="slide-number">11/20</div>
        <h2>Revenue Model: Transaction-Based</h2>
        
        <div className="metric-grid">
          <div className="metric-card">
            <div className="metric-number">2%</div>
            <div className="metric-label">Transaction fee on all property share trades</div>
          </div>
          <div className="metric-card">
            <div className="metric-number">$75/mo</div>
            <div className="metric-label">Platform fee for GusHome Renters</div>
          </div>
          <div className="metric-card">
            <div className="metric-number">FREE</div>
            <div className="metric-label">For landlords (zero direct fees)</div>
          </div>
        </div>
        
        <div className="financial-table">
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th>Properties</th>
                <th>Renters</th>
                <th>App Users</th>
                <th>Trading Rate</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>6</td>
                <td>120</td>
                <td>50</td>
                <td>75</td>
                <td>15%</td>
                <td>$8.2K</td>
              </tr>
              <tr>
                <td>9</td>
                <td>225</td>
                <td>90</td>
                <td>135</td>
                <td>20%</td>
                <td>$15.8K</td>
              </tr>
              <tr>
                <td>12</td>
                <td>375</td>
                <td>150</td>
                <td>225</td>
                <td>25%</td>
                <td>$28.5K</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    )
  },

  // Slide 12: Liquidity Strategy
  {
    id: 12,
    content: (
      <div className="slide">
        <div className="slide-number">12/20</div>
        <h2>Liquidity Bootstrapping Strategy</h2>
        
        <div className="three-column-layout">
          <div className="value-prop">
            <h4>Phase 1: Artificial (Mo 1-6)</h4>
            <ul>
              <li>$500K Liquidity Fund</li>
              <li>Guaranteed 20% purchases</li>
              <li>Build confidence</li>
            </ul>
          </div>
          
          <div className="value-prop">
            <h4>Phase 2: Organic (Mo 6-9)</h4>
            <ul>
              <li>Investor education</li>
              <li>FOMO creation</li>
              <li>Performance tracking</li>
            </ul>
          </div>
          
          <div className="value-prop">
            <h4>Phase 3: Self-Sustaining (Mo 9-12)</h4>
            <ul>
              <li>300+ properties</li>
              <li>Organic price discovery</li>
              <li>25% trading target</li>
            </ul>
          </div>
        </div>

        <div className="highlight">
          <h4>Success Metrics: Gradual transition from 5% → 25% organic trading rate</h4>
        </div>
      </div>
    )
  },

  // Slide 13: Legal Framework & IP Strategy
  {
    id: 13,
    content: (
      <div className="slide">
        <div className="slide-number">13/20</div>
        <h2>Legal Framework & IP Strategy</h2>
        
        <div className="two-column-layout">
          <div className="value-prop">
            <h4>NYC Legal Compliance ($1.2M)</h4>
            <ul>
              <li>SEC-compliant TIC structure</li>
              <li>NYC securities registration</li>
              <li>FINRA, Banking, Fair Housing</li>
            </ul>
          </div>
          
          <div className="value-prop">
            <h4>Patent Portfolio ($700K)</h4>
            <ul>
              <li>Core Business Model Patents</li>
              <li>Technology & Process Patents</li>
              <li>Defensive Patents</li>
            </ul>
          </div>
        </div>
        
        <div className="highlight">
          <h4>24+ Patent Applications Filed for Competitive Defense</h4>
          <p>Three-sided marketplace, rent-to-equity conversion, dynamic pricing, mobile voting, market making</p>
        </div>
      </div>
    )
  },

  // Slide 14: Financial Projections
  {
    id: 14,
    content: (
      <div className="slide">
        <div className="slide-number">14/20</div>
        <h2>5-Year Financial Projections</h2>
        
        <div className="financial-table">
          <table>
            <thead>
              <tr>
                <th>Metric</th>
                <th>Year 2</th>
                <th>Year 3</th>
                <th>Year 4</th>
                <th>Year 5</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Cities</td>
                <td>5</td>
                <td>12</td>
                <td>25</td>
                <td>50</td>
              </tr>
              <tr>
                <td>Properties</td>
                <td>1,875</td>
                <td>6,000</td>
                <td>12,000</td>
                <td>22,500</td>
              </tr>
              <tr>
                <td>Total Users</td>
                <td>1,875</td>
                <td>6,000</td>
                <td>12,000</td>
                <td>22,500</td>
              </tr>
              <tr>
                <td>Revenue</td>
                <td>$1.7M</td>
                <td>$5.4M</td>
                <td>$10.7M</td>
                <td>$20.0M</td>
              </tr>
              <tr>
                <td>EBITDA</td>
                <td>$(2.5M)</td>
                <td>$(2.4M)</td>
                <td>$(1.8M)</td>
                <td>$1.8M</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div className="highlight">
          <h4>Break-Even: Q4 Year 4 with validated marketplace model</h4>
        </div>
      </div>
    )
  },

  // Slide 15: Unit Economics
  {
    id: 15,
    content: (
      <div className="slide">
        <div className="slide-number">15/20</div>
        <h2>Unit Economics Validation</h2>
        
        <div className="metric-grid">
          <div className="metric-card">
            <div className="metric-number">$77.50</div>
            <div className="metric-label">Revenue per participating renter/month</div>
          </div>
          <div className="metric-card">
            <div className="metric-number">$10.50</div>
            <div className="metric-label">Revenue per app user/month</div>
          </div>
          <div className="metric-card">
            <div className="metric-number">$125</div>
            <div className="metric-label">Target customer acquisition cost</div>
          </div>
        </div>
        
        <div className="two-column-layout">
          <div className="value-prop">
            <h4>GusHome Renter Economics</h4>
            <ul>
              <li>40% opt-in rate (critical metric)</li>
              <li>$2,325 LTV over 2.5 years</li>
              <li>7.4:1 LTV/CAC ratio</li>
            </ul>
          </div>
          
          <div className="value-prop">
            <h4>App User Economics</h4>
            <ul>
              <li>$441 LTV over 3.5 years</li>
              <li>3.5:1 LTV/CAC ratio</li>
              <li>30% premium adoption</li>
            </ul>
          </div>
        </div>
      </div>
    )
  },

  // Slide 16: Risk Analysis & Mitigation
  {
    id: 16,
    content: (
      <div className="slide">
        <div className="slide-number">16/20</div>
        <h2>Risk Analysis & Mitigation</h2>
        
        <div className="three-column-layout">
          <div className="value-prop">
            <h4>Risk: Low Tenant Opt-In Rates</h4>
            <p><strong>Mitigation:</strong> A/B testing, education investment, flexible participation, 40% target allows 60% non-participation</p>
          </div>
          
          <div className="value-prop">
            <h4>Risk: Marketplace Liquidity Failure</h4>
            <p><strong>Mitigation:</strong> $500K liquidity fund, quality property selection, network effects, realistic 25% trading target</p>
          </div>
          
          <div className="value-prop">
            <h4>Risk: Regulatory Challenges</h4>
            <p><strong>Mitigation:</strong> $1.2M legal budget, early regulatory engagement, multiple legal opinions, backup structures</p>
          </div>
        </div>
        
        <div className="highlight">
          <h4>Post-Pilot: Significantly Reduced Risk Profile</h4>
          <p>Validated marketplace mechanics, proven user demand, operational legal framework</p>
        </div>
      </div>
    )
  },

  // Slide 17: Implementation Timeline
  {
    id: 17,
    content: (
      <div className="slide">
        <div className="slide-number">17/20</div>
        <h2>12-Month Implementation Timeline</h2>
        
        <div className="four-column-layout">
          <div className="timeline-phase">
            <h4>Months 1-3: Foundation</h4>
            <ul>
              <li>Legal framework and IP</li>
              <li>Core technology development</li>
              <li>Landlord partnership pipeline</li>
            </ul>
          </div>
          
          <div className="timeline-phase">
            <h4>Months 4-6: Pilot Launch</h4>
            <ul>
              <li>Beta with 3 landlord partners</li>
              <li>Public launch: 8 partners, 120 properties</li>
              <li>Initial opt-in and liquidity testing</li>
            </ul>
          </div>

          <div className="timeline-phase">
            <h4>Months 7-9: Scaling</h4>
            <ul>
              <li>Expand to 15 partners, 225 properties</li>
              <li>Target 20% trading rate</li>
              <li>Unit economics optimization</li>
            </ul>
          </div>
          
          <div className="timeline-phase">
            <h4>Months 10-12: Series A Prep</h4>
            <ul>
              <li>Reach 25 partners, 375 properties</li>
              <li>Achieve all validation metrics</li>
              <li>$28.5K monthly revenue</li>
            </ul>
          </div>
        </div>
      </div>
    )
  },

  // Slide 18: Funding Request
  {
    id: 18,
    content: (
      <div className="slide">
        <div className="slide-number">18/20</div>
        <h2>Funding Request: $4.8M Pre-Seed</h2>
        
        <div className="financial-table">
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Amount</th>
                <th>%</th>
                <th>Purpose</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>NYC Legal Framework</td>
                <td>$1.2M</td>
                <td>25%</td>
                <td>Enhanced regulatory compliance</td>
              </tr>
              <tr>
                <td>Technology Platform</td>
                <td>$1.2M</td>
                <td>25%</td>
                <td>Core platform + API integrations</td>
              </tr>
              <tr>
                <td>Pilot Operations</td>
                <td>$800K</td>
                <td>17%</td>
                <td>Landlord partnerships + liquidity fund</td>
              </tr>
              <tr>
                <td>Intellectual Property</td>
                <td>$700K</td>
                <td>15%</td>
                <td>24+ patent portfolio</td>
              </tr>
              <tr>
                <td>Team & Operations</td>
                <td>$650K</td>
                <td>14%</td>
                <td>15 employees, NYC-focused</td>
              </tr>
              <tr>
                <td>Marketing</td>
                <td>$200K</td>
                <td>4%</td>
                <td>Conservative pilot marketing</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    )
  },

  // Slide 19: Investment Terms & Returns
  {
    id: 19,
    content: (
      <div className="slide">
        <div className="slide-number">19/20</div>
        <h2>Investment Terms & Expected Returns</h2>
        
        <div className="metric-grid">
          <div className="metric-card">
            <div className="metric-number">$20M</div>
            <div className="metric-label">Valuation cap on convertible note</div>
          </div>
          <div className="metric-card">
            <div className="metric-number">20%</div>
            <div className="metric-label">Discount to Series A pricing</div>
          </div>
          <div className="metric-card">
            <div className="metric-number">6%</div>
            <div className="metric-label">Simple interest rate</div>
          </div>
        </div>
        
        <div className="value-prop">
          <h4>Series A Target (Month 15)</h4>
          <p>$18M raise at $80M pre-money (4.0x step-up from cap)</p>
        </div>
        
        <div className="financial-table">
          <table>
            <thead>
              <tr>
                <th>Scenario</th>
                <th>Probability</th>
                <th>Exit Timeline</th>
                <th>Exit Valuation</th>
                <th>Return Multiple</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Base Case</td>
                <td>60%</td>
                <td>5-7 years</td>
                <td>$800M-$1.5B</td>
                <td>35x-65x</td>
              </tr>
              <tr>
                <td>Optimistic</td>
                <td>25%</td>
                <td>4-6 years</td>
                <td>$2B-$4B</td>
                <td>85x-175x</td>
              </tr>
              <tr>
                <td>Conservative</td>
                <td>15%</td>
                <td>3-4 years</td>
                <td>$400M-$750M</td>
                <td>18x-35x</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    )
  },

  // Slide 20: Call to Action (Split into two slides)
  {
    id: 20,
    content: (
      <div className="slide">
        <div className="slide-number">20/21</div>
        <h2>Join Us in Building the NASDAQ for Apartments</h2>
        
        <div className="value-prop-grid">
          <div className="value-prop">
            <h4>✅ Proven Model</h4>
            <p>Stock market mechanics applied to $2.1T real estate market</p>
          </div>
          <div className="value-prop">
            <h4>✅ Perfect Timing</h4>
            <p>Housing crisis drives demand, technology enables execution</p>
          </div>
          <div className="value-prop">
            <h4>✅ Defensible IP</h4>
            <p>24+ patents creating competitive moats</p>
          </div>
          <div className="value-prop">
            <h4>✅ Massive TAM</h4>
            <p>$2.1T market with 40M+ users familiar with stock trading</p>
          </div>
        </div>
      </div>
    )
  },

  // Slide 21: Call to Action (Part 2)
  {
    id: 21,
    content: (
      <div className="slide">
        <div className="slide-number">21/21</div>
        <h2>Why Invest Now?</h2>
        
        <div className="cta">
          <div className="metric-grid">
            <div className="metric-card">
              <div className="metric-number">$4.8M</div>
              <div className="metric-label">Pre-Seed Investment</div>
            </div>
            <div className="metric-card">
              <div className="metric-number">35x-175x</div>
              <div className="metric-label">Expected Return Range</div>
            </div>
          </div>
          
          <div className="highlight">
            <h4>We're not asking you to bet on changing human behavior</h4>
            <p>We're asking you to fund the application of proven stock market mechanics to the world's largest asset class.</p>
          </div>
        </div>
      </div>
    )
  }
];

const PitchDeck = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Navigate to previous slide
  const goToPrevious = useCallback(() => {
    setCurrentSlide((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  // Navigate to next slide
  const goToNext = useCallback(() => {
    setCurrentSlide((prev) => (prev < slides.length - 1 ? prev + 1 : prev));
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNext, goToPrevious]);

  return (
    <div className="pitch-deck-container">
      {/* Main slide display */}
      <div className="slides-container">
        {slides[currentSlide].content}
      </div>

      {/* Left navigation arrow */}
      <button
        className={`nav-arrow nav-arrow-left ${currentSlide === 0 ? 'disabled' : ''}`}
        onClick={goToPrevious}
        disabled={currentSlide === 0}
      >
        ←
      </button>

      {/* Right navigation arrow */}
      <button
        className={`nav-arrow nav-arrow-right ${currentSlide === slides.length - 1 ? 'disabled' : ''}`}
        onClick={goToNext}
        disabled={currentSlide === slides.length - 1}
      >
        →
      </button>

      {/* Progress indicators */}
      <div className="progress-indicators">
        {slides.map((_, index) => (
          <div
            key={index}
            className={`progress-indicator ${currentSlide === index ? 'active' : ''}`}
            onClick={() => setCurrentSlide(index)}
          />
        ))}
      </div>

      <style jsx>{`
        /* Full-screen pitch deck styles */
        .pitch-deck-container {
          width: 100vw;
          height: 100vh;
          overflow: hidden;
          position: fixed;
          top: 0;
          left: 0;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: #fff;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          user-select: none;
          padding-top: 60px;
          box-sizing: border-box;
        }

        .slides-container {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .slide {
          width: 95%;
          max-width: 1400px;
          height: 95%;
          padding: 20px;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          text-align: center;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          position: relative;
          overflow-y: auto;
          box-sizing: border-box;
        }

        .slide-number {
          position: absolute;
          top: 20px;
          right: 20px;
          font-size: 0.9rem;
          opacity: 0.7;
          background: rgba(255, 255, 255, 0.2);
          padding: 5px 10px;
          border-radius: 15px;
        }

        /* Navigation arrows */
        .nav-arrow {
          position: fixed;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          cursor: pointer;
          font-size: 1.5rem;
          z-index: 1000;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
        }

        .nav-arrow:hover:not(.disabled) {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-50%) scale(1.1);
        }

        .nav-arrow.disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .nav-arrow-left {
          left: 20px;
        }

        .nav-arrow-right {
          right: 20px;
        }

        /* Progress indicators */
        .progress-indicators {
          position: fixed;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 8px;
          z-index: 1000;
        }

        .progress-indicator {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.3);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .progress-indicator.active {
          background: rgba(255, 255, 255, 0.8);
          transform: scale(1.2);
        }

        /* Typography */
        .slide h1 {
          font-size: clamp(2rem, 5vw, 3.5rem);
          font-weight: 800;
          margin-bottom: 15px;
          background: linear-gradient(135deg, #fff 0%, #f8f9ff 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .slide h2 {
          font-size: clamp(1.5rem, 4vw, 2.2rem);
          font-weight: 700;
          margin-bottom: 20px;
          color: #fff;
        }

        .slide h3 {
          font-size: clamp(1rem, 3vw, 1.4rem);
          font-weight: 600;
          margin-bottom: 15px;
          color: #f8f9ff;
        }

        .slide h4 {
          font-size: clamp(0.9rem, 2.5vw, 1.2rem);
          font-weight: 600;
          margin-bottom: 10px;
          color: #f8f9ff;
        }

        .slide p, .slide li {
          font-size: clamp(0.8rem, 2vw, 1rem);
          line-height: 1.4;
          margin-bottom: 10px;
          color: #f0f1ff;
        }

        .tagline {
          font-size: clamp(1rem, 3vw, 1.3rem);
          font-weight: 400;
          margin-bottom: 25px;
          opacity: 0.9;
        }

        .highlight {
          background: rgba(255, 255, 255, 0.2);
          padding: 10px;
          border-radius: 8px;
          margin: 10px 0;
          width: 100%;
          box-sizing: border-box;
        }

        .competitive-advantage {
          background: rgba(255, 255, 255, 0.15);
          padding: 12px;
          border-radius: 12px;
          margin: 12px 0;
          border-left: 4px solid rgba(255, 255, 255, 0.5);
          width: 100%;
          box-sizing: border-box;
        }

        .value-prop {
          background: rgba(255, 255, 255, 0.1);
          padding: 10px;
          border-radius: 8px;
          margin: 8px 0;
          width: 100%;
          box-sizing: border-box;
        }

        .value-prop-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 15px;
          width: 100%;
          margin: 20px 0;
        }

        .metric-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
          margin: 20px 0;
          width: 100%;
        }

        .metric-card {
          background: rgba(255, 255, 255, 0.2);
          padding: 20px;
          border-radius: 12px;
          text-align: center;
          backdrop-filter: blur(10px);
          min-height: 80px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .metric-number {
          font-size: clamp(1.8rem, 4vw, 2.8rem);
          font-weight: 800;
          color: #fff;
          margin-bottom: 8px;
        }

        .metric-label {
          font-size: clamp(0.8rem, 1.8vw, 1rem);
          color: #f0f1ff;
          line-height: 1.4;
        }

        .financial-table {
          width: 100%;
          margin: 20px 0;
          overflow-x: auto;
          font-size: clamp(0.8rem, 1.5vw, 0.95rem);
        }

        .financial-table table {
          width: 100%;
          border-collapse: collapse;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          overflow: hidden;
        }

        .financial-table th,
        .financial-table td {
          padding: 12px 8px;
          text-align: left;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        }

        .financial-table th {
          background: rgba(255, 255, 255, 0.2);
          font-weight: 600;
          color: #fff;
        }

        .financial-table td {
          color: #f0f1ff;
        }

        .two-column-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          width: 100%;
          margin: 15px 0;
        }

        .three-column-layout {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 15px;
          width: 100%;
          margin: 15px 0;
        }

        .four-column-layout {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 15px;
          width: 100%;
          margin: 15px 0;
        }

        .timeline-phase {
          background: rgba(255, 255, 255, 0.1);
          padding: 15px;
          border-radius: 10px;
        }

        .cta {
          margin-top: 15px;
          width: 100%;
        }

        ul {
          text-align: left;
          padding-left: 20px;
          margin: 8px 0;
        }

        li {
          margin-bottom: 8px;
        }

        /* Mobile responsive */
        @media (max-width: 768px) {
          .pitch-deck-container {
            height: 100vh;
            padding-top: 56px;
          }

          .slide {
            width: 98%;
            height: 92%;
            padding: 15px;
          }

          .nav-arrow {
            width: 45px;
            height: 45px;
            font-size: 1.3rem;
          }

          .nav-arrow-left {
            left: 10px;
          }

          .nav-arrow-right {
            right: 10px;
          }

          .metric-grid {
            grid-template-columns: 1fr;
            gap: 10px;
            margin: 15px 0;
          }

          .two-column-layout,
          .three-column-layout,
          .four-column-layout,
          .value-prop-grid {
            grid-template-columns: 1fr;
            gap: 10px;
            margin: 15px 0;
          }

          .financial-table th,
          .financial-table td {
            padding: 6px 4px;
            font-size: 0.7rem;
          }

          .highlight,
          .competitive-advantage,
          .value-prop {
            padding: 10px;
            margin: 8px 0;
          }

          .slide h1 {
            margin-bottom: 10px;
          }

          .slide h2 {
            margin-bottom: 15px;
          }

          .slide h3 {
            margin-bottom: 10px;
          }

          .slide h4 {
            margin-bottom: 8px;
          }

          .slide p, .slide li {
            margin-bottom: 8px;
          }

          .tagline {
            margin-bottom: 15px;
          }
        }
      `}</style>
    </div>
  );
};

export default PitchDeck;